<script setup lang="ts">
import { ref } from 'vue'
import type {
  IFilterParams,
  IDoesFilterPassParams,
} from 'ag-grid-community'
import { ParticipantRow } from '@/types/participant';
import Button from '../../Button.vue';

const { t } = useI18n();

enum EvalState {
  ALL = null,
  NOT_ACTIVATED = 0,
  NOT_CREATED = 1,
  READY = 2,
  STARTED = 3,
  FINISHED = 4,
}

const stateRecord = {
  [EvalState.ALL]: t('app.campaign.follow.headerButtons.filter.all'),
  [EvalState.NOT_ACTIVATED]: t('app.campaign.follow.headerButtons.filter.notActivated'),
  [EvalState.NOT_CREATED]: t('app.campaign.follow.headerButtons.filter.accountNotCreated'),
  [EvalState.READY]: t('app.campaign.follow.headerButtons.filter.pendingStart'),
  [EvalState.STARTED]: t('app.campaign.follow.headerButtons.filter.inProgress'),
  [EvalState.FINISHED]: t('app.campaign.follow.headerButtons.filter.availableResults'),

}

const props = defineProps<{
  params: IFilterParams<ParticipantRow>
}>();

const selectedState = ref<EvalState>(null);

function onChange(state: EvalState) {
  selectedState.value = state;
  props.params.filterChangedCallback();
  props.params.api.hideColumnFilter();
}

function isFilterActive() {
  return selectedState.value !== EvalState.ALL;
}

function doesFilterPass(params: IDoesFilterPassParams<ParticipantRow>) {
  const row = params.node.data

  if (selectedState.value === EvalState.ALL) {
    return true;
  }

  if (selectedState.value === EvalState.NOT_ACTIVATED && !row.progress.activated) return true;
  if (selectedState.value === EvalState.NOT_CREATED && row.progress.state === 0 && !row.progress.isCreated && row.progress.activated) return true;
  if (selectedState.value === EvalState.READY && row.progress.state === 0 && row.progress.isCreated && row.progress.activated) return true;
  if (selectedState.value === EvalState.STARTED && row.progress.state === 1) return true;
  if (selectedState.value === EvalState.FINISHED && row.progress.state === 2) return true;
  return false;
}

function getModel() {
  if (selectedState.value === EvalState.ALL) {
    return null;
  }

  return {
    state: selectedState.value,
  }
}

function setModel(model: { state: EvalState } | null) {
  selectedState.value = model?.state ?? null
}

defineExpose({
  isFilterActive,
  doesFilterPass,
  getModel,
  setModel,
});
</script>

<template>
  <div class="progress-filter-selector">
    <div class="menu-option" :class="{ 'active': selectedState === EvalState.ALL}" @click="onChange(EvalState.ALL)">{{ $t('app.campaign.follow.headerButtons.filter.all') }}</div>
    <div class="menu-option" :class="{ 'active': selectedState === EvalState.NOT_ACTIVATED}" @click="onChange(EvalState.NOT_ACTIVATED)">{{ $t('app.campaign.follow.headerButtons.filter.notActivated') }}</div>
    <div class="menu-option" :class="{ 'active': selectedState === EvalState.NOT_CREATED}" @click="onChange(EvalState.NOT_CREATED)">{{ $t('app.campaign.follow.headerButtons.filter.accountNotCreated') }}</div>
    <div class="menu-option" :class="{ 'active': selectedState === EvalState.READY}" @click="onChange(EvalState.READY)">{{ $t('app.campaign.follow.headerButtons.filter.pendingStart') }}</div>
    <div class="menu-option" :class="{ 'active': selectedState === EvalState.STARTED}" @click="onChange(EvalState.STARTED)">{{ $t('app.campaign.follow.headerButtons.filter.inProgress') }}</div>
    <div class="menu-option" :class="{ 'active': selectedState === EvalState.FINISHED}" @click="onChange(EvalState.FINISHED)">{{ $t('app.campaign.follow.headerButtons.filter.availableResults') }}</div>
  </div>
</template>

<style lang="scss">
@import "@/assets/style/variables.scss";

.ag-menu {
  margin-top: 4px;
}

.progress-filter-selector {
  .menu-option {
    padding: 4px 8px;
    cursor: pointer;
    font-weight: 500;

    &:hover {
      background-color: var(--primary-300-color) !important;
    }

    &.active {
      background-color: var(--primary-100-color);
    }
  }
}
</style>